import React from 'react';
import PropTypes from 'prop-types';

const DASH = '—';

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : null;

const money = (value) => (value === null || value === undefined || value === '' ? null : `$ ${value}`);

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ') : null);

const Row = ({ label, value, mono = false }) => (
  <div className="detail-row">
    <span className="detail-label">{label}</span>
    <span className={mono ? 'detail-value mono' : 'detail-value'}>{value === null || value === undefined || value === '' ? DASH : value}</span>
  </div>
);

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  mono: PropTypes.bool,
};

const ExternalLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer" className="detail-link">
    {children}
  </a>
);

ExternalLink.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// One card per shipment leg. The outbound kit and the returning sample are
// separate packages and can travel with different carriers, so they never share
// a card.
const ShipmentGroup = ({ title, legs, emptyText }) => (
  <div className="kit-detail-group">
    <h4>{title}</h4>
    {legs.length === 0 && <div className="detail-empty">{emptyText}</div>}
    {legs.map((leg) => (
      <div key={`${leg.kit}-${leg.direction}`} className="shipment-leg">
        <Row label="Kit" value={leg.kit_label} />
        <Row label="Carrier" value={leg.carrier} />
        <Row
          label="Tracking"
          value={leg.tracking_url ? <ExternalLink href={leg.tracking_url}>{leg.tracking_number}</ExternalLink> : leg.tracking_number}
          mono
        />
        <Row label="Status" value={leg.status} />
        <Row label="Shipped" value={formatDateTime(leg.shipped_at)} />
        <Row label="Delivered" value={formatDateTime(leg.delivered_at)} />
      </div>
    ))}
  </div>
);

ShipmentGroup.propTypes = {
  title: PropTypes.string.isRequired,
  legs: PropTypes.arrayOf(PropTypes.object).isRequired,
  emptyText: PropTypes.string.isRequired,
};

// Full B2B (clinic) order picture for staff. `details` is the staff-only
// `b2b_details` block the orders API attaches to clinic orders.
const ClinicOrderDetails = ({ details, paymentLink = null, documents = [] }) => {
  if (!details) return null;

  const {
    clinic,
    ordered_by: orderedBy,
    patient,
    product,
    billing,
    external_ids: ids,
    shipments,
    timeline,
    cancellation,
    replacement,
    results,
  } = details;
  const payment = billing?.payment;

  return (
    <div className="kit-details clinic-order-details">
      <h3>Clinic Order Details</h3>

      {cancellation && (
        <div className="kit-detail-error">
          <strong>Canceled</strong> on {formatDateTime(cancellation.cancelled_at)}
          {cancellation.cancelled_by && <> by {cancellation.cancelled_by}</>}
          {cancellation.reason && (
            <>
              <br />
              Reason: {cancellation.reason}
            </>
          )}
        </div>
      )}

      <div className="kit-detail-groups">
        <div className="kit-detail-group">
          <h4>Clinic</h4>
          <Row label="Name" value={clinic?.name} />
          <Row label="Clinic ID" value={clinic?.swimscore_clinic_id} mono />
          <Row label="Email" value={clinic?.email} />
          <Row label="Phone" value={clinic?.phone} />
          <Row label="Payment Model" value={clinic?.payment_model_label} />
        </div>

        <div className="kit-detail-group">
          <h4>Ordered By</h4>
          <Row label="Name" value={orderedBy?.name} />
          <Row label="Role" value={orderedBy?.role} />
          <Row label="Email" value={orderedBy?.email} />
          <Row label="Phone" value={orderedBy?.phone} />
        </div>

        <div className="kit-detail-group">
          <h4>Patient Contact</h4>
          <Row label="Name" value={patient?.name} />
          <Row label="Email" value={patient?.email} />
          <Row label="Phone" value={patient?.phone} />
          <Row label="Date of Birth" value={formatDate(patient?.dob)} />
        </div>

        <div className="kit-detail-group">
          <h4>Product & Amount</h4>
          <Row label="Product" value={product?.name} />
          <Row label="SKU" value={product?.sku} mono />
          <Row label="Quantity" value={product?.quantity} />
          <Row label="Order Amount" value={money(product?.price)} />
          <Row label="SwimScore Cost" value={money(product?.cost)} />
          <Row label="DNA Fragmentation" value={product?.has_dna ? 'Yes' : 'No'} />
        </div>

        <div className="kit-detail-group">
          <h4>Billing & Payment</h4>
          <Row label="Payment Model" value={billing?.billing_type_label} />
          <Row label="Clinic Portal Shows" value={billing?.payment_label} />
          <Row
            label="Payment Status"
            value={payment && <span className={`payment-state ${payment.completed ? 'done' : payment.state}`}>{payment.label}</span>}
          />
          <Row label="Paid At" value={formatDateTime(payment?.completed_at)} />
          <Row label="Clinic Invoice" value={capitalize(billing?.clinic_invoice_status)} />
          {billing?.invoice && (
            <>
              <Row label="Invoice #" value={billing.invoice.number} mono />
              <Row label="Invoice Status" value={capitalize(billing.invoice.status)} />
              <Row label="Invoice Period" value={`${formatDate(billing.invoice.period_start)} - ${formatDate(billing.invoice.period_end)}`} />
            </>
          )}
          <Row label="Stripe Payment" value={billing?.stripe_payment_intent} mono />
          {paymentLink && <Row label="Payment Link" value={<ExternalLink href={paymentLink}>Open Payment Link</ExternalLink>} />}
        </div>

        <div className="kit-detail-group">
          <h4>Order IDs</h4>
          <Row label="SwimScore Order" value={ids?.swimscore_order_id} mono />
          <Row label="MaleMan Order" value={ids?.mailman_order_id} mono />
          <Row label="Tasso Order" value={ids?.tasso_order_id} mono />
          <Row label="MDI Case" value={ids?.mdi_case_id} mono />
        </div>
      </div>

      <h3 className="clinic-order-subheading">Shipping</h3>
      <div className="kit-detail-groups">
        <ShipmentGroup title="Outbound Kit (to Patient)" legs={shipments?.outbound || []} emptyText="Kit not shipped yet" />
        <ShipmentGroup title="Return Sample (to Lab)" legs={shipments?.return || []} emptyText="No return tracking yet" />
      </div>

      <h3 className="clinic-order-subheading">Order Timeline</h3>
      <ol className="clinic-timeline">
        {(timeline || []).map((step) => (
          <li key={step.key} className={`clinic-timeline-step ${step.completed ? 'done' : ''} ${step.key === 'canceled' ? 'canceled' : ''}`}>
            <span className="clinic-timeline-dot" />
            <span className="clinic-timeline-label">{step.label}</span>
            <span className="clinic-timeline-date">{formatDateTime(step.occurred_at) || (step.completed ? '' : 'Pending')}</span>
          </li>
        ))}
      </ol>

      {replacement && (
        <div className="clinic-order-note">
          <strong>Replacement:</strong> {replacement.kit} replaced on {formatDateTime(replacement.replaced_at)}
        </div>
      )}

      <h3 className="clinic-order-subheading">Results</h3>
      <div className="kit-detail-groups">
        <div className="kit-detail-group">
          <h4>Status</h4>
          <Row label="Semen Results Ready" value={formatDateTime(results?.semen_results_ready_at)} />
          <Row label="Hormone Results Ready" value={formatDateTime(results?.hormone_results_ready_at)} />
          <Row label="Clinic Review" value={capitalize(results?.review_status)} />
          <Row label="Clinic Notified" value={formatDateTime(results?.clinic_notified_at)} />
        </div>
        <div className="kit-detail-group">
          <h4>Result PDFs</h4>
          {documents.length === 0 && <div className="detail-empty">No results yet</div>}
          {documents.map((doc) => (
            <Row
              key={doc.id}
              label={doc.display_title || doc.title || 'Report'}
              value={
                doc.view_url || doc.download_url ? (
                  <ExternalLink href={doc.view_url || doc.download_url}>Open PDF</ExternalLink>
                ) : (
                  formatDateTime(doc.uploaded_at)
                )
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

ClinicOrderDetails.propTypes = {
  details: PropTypes.object,
  paymentLink: PropTypes.string,
  documents: PropTypes.arrayOf(PropTypes.object),
};

export default ClinicOrderDetails;
